import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

export interface Profile { id: string; kind: 'parent' | 'student'; display_name: string; avatar: string; grade: number | null; status: string }
interface AuthState {
  session: Session | null; profile: Profile | null; isAdmin: boolean; loading: boolean;
  roles: string[]; isTeacher: boolean; isEditor: boolean; isApprover: boolean;
  refresh: () => Promise<void>; signOut: (scope?: 'local' | 'global') => Promise<void>;
}
const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const isAdmin = roles.includes('admin');
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();
  const userId = useRef<string | null>(null);

  const load = useCallback(async (s: Session | null) => {
    // Інший користувач на тому ж пристрої (батьки ↔ дитина): дані попереднього не показуємо навіть із кешу.
    if ((s?.user.id ?? null) !== userId.current) { userId.current = s?.user.id ?? null; qc.clear(); }
    setSession(s);
    if (!s) { setProfile(null); setRoles([]); setLoading(false); return; }
    const [{ data: p }, { data: roles }] = await Promise.all([
      supabase.from('profiles').select('id, kind, display_name, avatar, grade, status').eq('id', s.user.id).maybeSingle(),
      supabase.from('user_roles').select('role').eq('user_id', s.user.id),
    ]);
    setProfile((p as Profile) ?? null);
    setRoles((roles ?? []).map((r) => String(r.role)));
    setLoading(false);
  }, [qc]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => load(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // Нові токени того самого користувача (оновлення або нова сесія від сервера, напр. після зміни пароля дитини):
      // профіль не перечитуємо й сторінку не перемонтовуємо.
      if (event === 'TOKEN_REFRESHED' || (event === 'SIGNED_IN' && s?.user.id && s.user.id === userId.current)) { setSession(s); return; }
      setLoading(true); void load(s);
    });
    return () => sub.subscription.unsubscribe();
  }, [load]);

  const value: AuthState = {
    session, profile, isAdmin, loading, roles,
    isTeacher: roles.includes('teacher'),
    isEditor: roles.some((r) => ['teacher', 'methodist', 'admin'].includes(r)),
    isApprover: roles.some((r) => ['methodist', 'admin'].includes(r)),
    refresh: async () => { const { data } = await supabase.auth.getSession(); await load(data.session); },
    signOut: async (scope = 'local') => { await supabase.auth.signOut({ scope }); },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

export const homeFor = (p: Profile | null) => (p?.kind === 'student' ? '/app' : '/parent/children');
